import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_BASE = '';

const TECH_FIELDS = [
  'Software / AI / Machine Learning', 'Biotechnology / Pharma', 'Hardware / Electronics',
  'Mechanical Engineering', 'Chemical / Materials', 'Telecommunications',
  'Medical Devices', 'Clean Energy / Sustainability', 'Fintech / Blockchain', 'Other'
];

function StepProgress({ steps }) {
  if (!steps?.length) return null;
  const completed = steps.filter(s => s.status === 'completed').length;
  const pct = Math.round((completed / steps.length) * 100);
  return (
    <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: 12, padding: 20, marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, color: '#334155' }}>Analysis Progress</span>
        <span style={{ fontWeight: 600, color: '#1565c0' }}>{pct}%</span>
      </div>
      <div style={{ height: 8, background: '#e0e0e0', borderRadius: 4, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: '#1565c0', borderRadius: 4, transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {steps.map((step, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 'bold', color: 'white',
              background: step.status === 'completed' ? '#2e7d32' : step.status === 'active' ? '#1565c0' : '#9e9e9e',
              animation: step.status === 'active' ? 'pulse 1.5s infinite' : 'none',
            }}>
              {step.status === 'completed' ? '✓' : step.status === 'active' ? '●' : '○'}
            </span>
            <span style={{ color: step.status === 'completed' ? '#2e7d32' : step.status === 'active' ? '#1565c0' : '#9e9e9e', fontWeight: step.status === 'active' ? 600 : 400 }}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SeverityBadge({ level }) {
  const colors = { high: { bg: '#fef2f2', color: '#dc2626' }, medium: { bg: '#fef3c7', color: '#d97706' }, low: { bg: '#ecfdf5', color: '#059669' } };
  const c = colors[level] || colors.medium;
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: c.bg, color: c.color }}>{level}</span>;
}

function ScoreBadge({ score }) {
  const color = score >= 7 ? '#dc2626' : score >= 4 ? '#d97706' : '#059669';
  return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', background: color, color: 'white', fontSize: 13, fontWeight: 700 }}>{score}</span>;
}

export default function PatentAnalysis() {
  const { user } = useAuth();
  const [wizardStep, setWizardStep] = useState(1);
  const [ideaDescription, setIdeaDescription] = useState('');
  const [technologyField, setTechnologyField] = useState('');
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { if (user) fetchAnalyses(); }, [user]);

  const fetchAnalyses = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/patents`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setAnalyses(data.analyses);
    } catch (e) { console.error(e); }
  };

  const pollResult = useCallback((uuid) => {
    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/patents/${uuid}`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setCurrentAnalysis(data.analysis);
          if (data.analysis.status === 'completed' || data.analysis.status === 'failed') {
            clearInterval(poll);
            setWizardStep(3);
            fetchAnalyses();
          }
        }
      } catch { clearInterval(poll); }
    }, 2000);
    return () => clearInterval(poll);
  }, []);

  const handleCheckIdea = async () => {
    setLoading(true);
    setWizardStep(2);
    try {
      const res = await fetch(`${API_BASE}/api/patents/check-idea`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ideaDescription, technologyField }),
      });
      const data = await res.json();
      if (data.success) {
        setCurrentAnalysis({ uuid: data.uuid, status: 'processing', steps: [] });
        pollResult(data.uuid);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleDraftClaims = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/patents/draft-claims`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inventionDescription: ideaDescription, type: 'utility' }),
      });
      const data = await res.json();
      if (data.success) {
        setCurrentAnalysis({ uuid: data.uuid, status: 'processing', steps: [] });
        pollResult(data.uuid);
        setWizardStep(4);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadAnalysis = (a) => {
    setCurrentAnalysis(a);
    setWizardStep(a.status === 'completed' ? 3 : 2);
    setShowHistory(false);
  };

  const reset = () => { setWizardStep(1); setCurrentAnalysis(null); setIdeaDescription(''); setTechnologyField(''); };

  const r = currentAnalysis?.results || {};
  const novelty = r.novelty || {};
  const isNovel = novelty.isLikelyNovel || novelty.verdict === 'novel';
  const licensing = r.licensing || {};
  const searchResults = r.search?.existingPatents || [];

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: '#1e293b', margin: 0 }}>Patent Analysis</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Check if your idea is patented, explore licensing, or file a new patent</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowHistory(!showHistory)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: showHistory ? '#eff6ff' : 'white', color: '#475569', cursor: 'pointer', fontSize: 14 }}>
            History ({analyses.length})
          </button>
          {wizardStep > 1 && <button onClick={reset} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#f1f5f9', color: '#475569', cursor: 'pointer', fontSize: 14 }}>New Search</button>}
        </div>
      </div>

      {/* Wizard Steps Indicator */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32, gap: 0 }}>
        {['Describe Your Idea', 'Searching Patents', 'Results & Next Steps'].map((label, i) => (
          <React.Fragment key={i}>
            {i > 0 && <div style={{ flex: 1, height: 2, background: wizardStep > i ? '#1565c0' : '#e0e0e0' }} />}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: wizardStep > i + 1 ? '#2e7d32' : wizardStep === i + 1 ? '#1565c0' : '#e0e0e0',
                color: 'white', fontWeight: 700, fontSize: 14, marginBottom: 6,
              }}>
                {wizardStep > i + 1 ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: 12, color: wizardStep === i + 1 ? '#1565c0' : '#9e9e9e', fontWeight: wizardStep === i + 1 ? 600 : 400, textAlign: 'center' }}>{label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* History Sidebar */}
      {showHistory && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 16, marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Previous Analyses</h3>
          {analyses.length === 0 ? <p style={{ color: '#9e9e9e' }}>No previous analyses</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
              {analyses.map(a => (
                <div key={a.uuid} onClick={() => loadAnalysis(a)} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{a.title || a.type}</div>
                    <div style={{ fontSize: 12, color: '#9e9e9e' }}>{new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: a.status === 'completed' ? '#ecfdf5' : a.status === 'failed' ? '#fef2f2' : '#fef3c7', color: a.status === 'completed' ? '#059669' : a.status === 'failed' ? '#dc2626' : '#d97706' }}>{a.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 1: Describe Idea */}
      {wizardStep === 1 && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 32 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>Describe Your Idea or Feature</h2>
          <p style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>We'll search patent databases to see if it's already protected and guide you on next steps.</p>

          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Your idea or invention *</label>
          <textarea
            value={ideaDescription} onChange={e => setIdeaDescription(e.target.value)}
            placeholder="Describe your idea, feature, or invention in detail. The more specific, the better the patent search results..."
            style={{ width: '100%', minHeight: 140, padding: '12px 16px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15, resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
          />

          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 6, marginTop: 16 }}>Technology field</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {TECH_FIELDS.map(f => (
              <button key={f} onClick={() => setTechnologyField(f === technologyField ? '' : f)}
                style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid', borderColor: technologyField === f ? '#1565c0' : '#e2e8f0', background: technologyField === f ? '#eff6ff' : 'white', color: technologyField === f ? '#1565c0' : '#64748b', cursor: 'pointer', fontSize: 13, fontWeight: technologyField === f ? 600 : 400 }}>
                {f}
              </button>
            ))}
          </div>

          <button onClick={handleCheckIdea} disabled={!ideaDescription.trim() || loading}
            style={{ marginTop: 24, padding: '12px 32px', borderRadius: 8, border: 'none', background: '#1565c0', color: 'white', fontSize: 16, fontWeight: 600, cursor: 'pointer', opacity: !ideaDescription.trim() ? 0.5 : 1 }}>
            Check Patent Status
          </button>
        </div>
      )}

      {/* Step 2: Searching */}
      {wizardStep === 2 && currentAnalysis && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 32 }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Searching Patent Databases...</h2>
          <StepProgress steps={currentAnalysis.steps} />
          <p style={{ color: '#64748b', fontSize: 14 }}>Analyzing your idea against existing patents. This typically takes 30-60 seconds.</p>
        </div>
      )}

      {/* Step 3: Results */}
      {wizardStep === 3 && currentAnalysis?.status === 'completed' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Novelty Verdict */}
          <div style={{ background: isNovel ? '#ecfdf5' : '#fef2f2', borderRadius: 12, padding: 24, border: `2px solid ${isNovel ? '#a7f3d0' : '#fecaca'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{ fontSize: 32 }}>{isNovel ? '🟢' : '🔴'}</span>
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: isNovel ? '#065f46' : '#991b1b', margin: 0 }}>
                  {isNovel ? 'Your Idea Appears Novel!' : 'Similar Patents Found'}
                </h2>
                <p style={{ color: isNovel ? '#047857' : '#b91c1c', margin: 0, marginTop: 4 }}>
                  Novelty Score: {novelty.noveltyScore || 'N/A'}/10 — {novelty.reasoning || novelty.verdict}
                </p>
              </div>
            </div>
          </div>

          {/* Existing Patents Found */}
          {searchResults.length > 0 && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Related Patents ({searchResults.length})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {searchResults.slice(0, 8).map((p, i) => (
                  <div key={i} style={{ padding: 16, borderRadius: 8, background: '#f8fafc', borderLeft: `4px solid ${p.relevanceScore >= 7 ? '#dc2626' : p.relevanceScore >= 4 ? '#d97706' : '#059669'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.title}</div>
                        <div style={{ fontSize: 13, color: '#64748b' }}>
                          {p.patentNumber && <a href={`https://patents.google.com/patent/${p.patentNumber.replace(/[^A-Za-z0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ marginRight: 12, color: '#1565c0', textDecoration: 'none' }}>#{p.patentNumber} ↗</a>}
                          {p.assignee && <span style={{ marginRight: 12 }}>{p.assignee}</span>}
                          {p.filingDate && <span>{p.filingDate}</span>}
                        </div>
                        {p.abstract && <p style={{ fontSize: 13, color: '#475569', marginTop: 8, lineHeight: 1.5 }}>{p.abstract.substring(0, 200)}...</p>}
                        {p.claimOverlap && <p style={{ fontSize: 13, color: '#92400e', marginTop: 4 }}>Overlap: {p.claimOverlap}</p>}
                      </div>
                      <ScoreBadge score={p.relevanceScore} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Licensing Info (if not novel) */}
          {!isNovel && licensing.estimatedLicensingCost && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Licensing Options</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
                <div style={{ padding: 16, borderRadius: 8, background: '#f8fafc', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>One-Time License</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{licensing.estimatedLicensingCost.oneTime || 'Varies'}</div>
                </div>
                <div style={{ padding: 16, borderRadius: 8, background: '#f8fafc', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Royalty Rate</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{licensing.estimatedLicensingCost.royaltyRate || 'Varies'}</div>
                </div>
                <div style={{ padding: 16, borderRadius: 8, background: '#f8fafc', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Typical Range</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{licensing.estimatedLicensingCost.range || 'Varies'}</div>
                </div>
              </div>

              {licensing.designAroundOptions?.length > 0 && (
                <>
                  <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Design-Around Options</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {licensing.designAroundOptions.map((d, i) => (
                      <div key={i} style={{ padding: 12, borderRadius: 8, background: '#f8fafc', borderLeft: '4px solid #6366f1' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 500 }}>{d.approach}</span>
                          <SeverityBadge level={d.feasibility} />
                        </div>
                        {d.description && <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{d.description}</p>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Filing Recommendation (if novel) */}
          {isNovel && licensing.filingRecommendation && (
            <div style={{ background: 'white', borderRadius: 12, border: '2px solid #a7f3d0', padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#065f46' }}>File a Patent Application</h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 20 }}>
                <div style={{ padding: 16, borderRadius: 8, background: '#ecfdf5', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#047857', marginBottom: 4 }}>Provisional Filing</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#065f46' }}>${licensing.filingRecommendation.estimatedFilingCost?.provisional || '—'}</div>
                </div>
                <div style={{ padding: 16, borderRadius: 8, background: '#ecfdf5', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#047857', marginBottom: 4 }}>Non-Provisional</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#065f46' }}>${licensing.filingRecommendation.estimatedFilingCost?.nonprovisional || '—'}</div>
                </div>
                <div style={{ padding: 16, borderRadius: 8, background: '#ecfdf5', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#047857', marginBottom: 4 }}>Attorney Fees</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#065f46' }}>{licensing.filingRecommendation.estimatedFilingCost?.attorneyFees || '—'}</div>
                </div>
                <div style={{ padding: 16, borderRadius: 8, background: '#ecfdf5', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#047857', marginBottom: 4 }}>Total Estimate</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#065f46' }}>{licensing.filingRecommendation.estimatedFilingCost?.totalEstimate || '—'}</div>
                </div>
              </div>

              {licensing.filingRecommendation.timeline && (
                <p style={{ fontSize: 14, color: '#047857', marginBottom: 16 }}>Timeline: {licensing.filingRecommendation.timeline}</p>
              )}

              {licensing.filingRecommendation.nextSteps?.length > 0 && (
                <>
                  <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Next Steps</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {licensing.filingRecommendation.nextSteps.map((step, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', borderRadius: 8, background: '#f0fdf4' }}>
                        <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#059669', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                        <span style={{ fontSize: 14 }}>{step}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <button onClick={handleDraftClaims} disabled={loading}
                style={{ marginTop: 20, padding: '12px 28px', borderRadius: 8, border: 'none', background: '#059669', color: 'white', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                Draft Patent Claims
              </button>
            </div>
          )}

          {/* Key Differences / Overlaps */}
          {(novelty.keyDifferences?.length > 0 || novelty.keyOverlaps?.length > 0) && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Analysis Details</h3>
              {novelty.keyDifferences?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#059669', marginBottom: 8 }}>What Makes Your Idea Different</h4>
                  {novelty.keyDifferences.map((d, i) => (
                    <div key={i} style={{ padding: '6px 12px', borderRadius: 6, background: '#f0fdf4', marginBottom: 4, fontSize: 14 }}>+ {d}</div>
                  ))}
                </div>
              )}
              {novelty.keyOverlaps?.length > 0 && (
                <div>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#dc2626', marginBottom: 8 }}>Areas of Overlap</h4>
                  {novelty.keyOverlaps.map((d, i) => (
                    <div key={i} style={{ padding: '6px 12px', borderRadius: 6, background: '#fef2f2', marginBottom: 4, fontSize: 14 }}>- {d}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step 4: Claims Drafting */}
      {wizardStep === 4 && currentAnalysis && (
        <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', padding: 32 }}>
          {currentAnalysis.status === 'processing' ? (
            <>
              <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>Drafting Patent Claims...</h2>
              <StepProgress steps={currentAnalysis.steps} />
            </>
          ) : currentAnalysis.status === 'completed' && currentAnalysis.results?.claims ? (
            <>
              <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 20 }}>Draft Patent Claims</h2>
              {currentAnalysis.results.claims.independentClaims?.map((c, i) => (
                <div key={i} style={{ padding: 16, borderRadius: 8, background: '#f8fafc', borderLeft: '4px solid #6366f1', marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', marginBottom: 4 }}>Independent Claim {c.claimNumber} ({c.type})</div>
                  <p style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{c.text}</p>
                </div>
              ))}
              {currentAnalysis.results.claims.dependentClaims?.map((c, i) => (
                <div key={i} style={{ padding: 16, borderRadius: 8, background: '#f8fafc', borderLeft: '4px solid #a5b4fc', marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#818cf8', marginBottom: 4 }}>Dependent Claim {c.claimNumber} (depends on {c.dependsOn})</div>
                  <p style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{c.text}</p>
                </div>
              ))}
              {currentAnalysis.results.specification && (
                <div style={{ marginTop: 20, padding: 20, borderRadius: 8, background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                  <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Abstract</h4>
                  <p style={{ fontSize: 14, lineHeight: 1.6 }}>{currentAnalysis.results.specification.abstract}</p>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* Failed state */}
      {currentAnalysis?.status === 'failed' && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 20 }}>
          <p style={{ color: '#dc2626', fontWeight: 500 }}>Analysis failed: {currentAnalysis.results?.error || 'Unknown error'}</p>
          <button onClick={reset} style={{ marginTop: 12, padding: '8px 20px', borderRadius: 8, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer' }}>Try Again</button>
        </div>
      )}

      <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, marginTop: 32 }}>
        This is AI-assisted patent research, not legal advice. Consult a patent attorney before filing.
      </p>
    </div>
  );
}
